import { useTranslation } from 'react-i18next';

export const Footer = () => {
  const { t, i18n } = useTranslation();

  return (
    <footer className="bg-white text-gray-600 pt-16 pb-8 border-t border-gray-100 relative overflow-hidden">
      {/* Subtle gradient accent at top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-electric-blue/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-8 lg:mb-16">
          {/* Brand Column */}
          <div className="space-y-6 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3">
              <img
                src="/logo_transparente.webp"
                alt="4tlayer"
                className="h-10 w-auto object-contain"
              />
            </div>

            <p className="text-gray-500 text-sm leading-relaxed hidden md:block font-montserrat font-semibold">
              {t('footer.brand_desc')}
            </p>

            <div className="flex justify-center md:justify-start gap-4">
              {[
                { icon: 'linkedin-in', label: 'LinkedIn' },
                { icon: 'twitter', label: 'Twitter' },
                { icon: 'telegram-plane', label: 'Telegram' }
              ].map((social) => (
                <a
                  key={social.label}
                  href="#"
                  className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gradient-to-r hover:from-brand-electric-blue hover:to-brand-neon-green hover:text-white hover:border-transparent hover:shadow-glow-blue transition-all duration-300 transform hover:-translate-y-1"
                  aria-label={social.label}
                >
                  <i className={`fab fa-${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Product Links */}
          <div className="hidden md:block">
            <h4 className="text-gray-900 font-bold mb-6 font-montserrat">{t('footer.product')}</h4>
            <ul className="space-y-4">
              <li><a href="#como-funciona" className="text-gray-500 hover:text-brand-electric-blue transition-colors flex items-center gap-2 font-montserrat font-semibold text-sm"><i className="fas fa-chevron-right text-xs opacity-50"></i> {t('nav.howItWorks')}</a></li>
              <li><a href="#validacion" className="text-gray-500 hover:text-brand-electric-blue transition-colors flex items-center gap-2 font-montserrat font-semibold text-sm"><i className="fas fa-chevron-right text-xs opacity-50"></i> {t('comparison.headers.verificacion')}</a></li>
              <li><a href="#beneficios" className="text-gray-500 hover:text-brand-electric-blue transition-colors flex items-center gap-2 font-montserrat font-semibold text-sm"><i className="fas fa-chevron-right text-xs opacity-50"></i> {t('benefits.title')}</a></li>
              <li><a href="https://app.truedok.com/login" className="text-gray-500 hover:text-brand-electric-blue transition-colors flex items-center gap-2 font-montserrat font-semibold text-sm"><i className="fas fa-chevron-right text-xs opacity-50"></i> {t('footer.links.access')}</a></li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="hidden md:block">
            <h4 className="text-gray-900 font-bold mb-6 font-montserrat">{t('footer.legal')}</h4>
            <ul className="space-y-4">
              <li><a href="#" className="text-gray-500 hover:text-brand-electric-blue transition-colors font-montserrat font-semibold text-sm">{t('footer.legal_links.terms')}</a></li>
              <li><a href="#" className="text-gray-500 hover:text-brand-electric-blue transition-colors font-montserrat font-semibold text-sm">{t('footer.legal_links.privacy')}</a></li>
              <li><a href="#" className="text-gray-500 hover:text-brand-electric-blue transition-colors font-montserrat font-semibold text-sm">{t('footer.legal_links.cookies')}</a></li>
              <li><a href="#" className="text-gray-500 hover:text-brand-electric-blue transition-colors font-montserrat font-semibold text-sm">{t('footer.legal_links.compliance')}</a></li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="hidden md:block">
            <h4 className="text-gray-900 font-bold mb-6 font-montserrat">{t('footer.contact')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-gray-500">
                <i className="fas fa-map-marker-alt mt-1 text-brand-electric-blue"></i>
                <span className="text-sm font-montserrat font-semibold">Av. Tecnológica 123, Edificio Blockchain, Madrid</span>
              </li>
              <li className="flex items-center gap-3 text-gray-500">
                <i className="fas fa-envelope text-brand-electric-blue"></i>
                <span className="text-sm font-montserrat font-semibold">info@4tlayer.com</span>
              </li>
              <li className="flex items-center gap-3 text-gray-500">
                <i className="fas fa-phone text-brand-electric-blue"></i>
                <span className="text-sm font-montserrat font-semibold">+1 (954) 892-4361</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm text-center md:text-left font-montserrat font-semibold">
            &copy; {new Date().getFullYear()} 4tlayer. {t('footer.rights')}
          </p>
          <div className="flex gap-6 text-sm text-gray-400 font-montserrat font-semibold">
            <button 
              onClick={() => i18n.changeLanguage('es')}
              className={`hover:text-brand-electric-blue transition-colors ${i18n.language === 'es' ? 'text-brand-electric-blue' : ''}`}
            >
              Español
            </button>
            <button 
              onClick={() => i18n.changeLanguage('en')}
              className={`hover:text-brand-electric-blue transition-colors ${i18n.language === 'en' ? 'text-brand-electric-blue' : ''}`}
            >
              English
            </button>
            <button 
              onClick={() => i18n.changeLanguage('eu')}
              className={`hover:text-brand-electric-blue transition-colors ${i18n.language === 'eu' ? 'text-brand-electric-blue' : ''}`}
            >
              Euskera
            </button>
          </div>
        </div>
      </div>

      {/* Background decorative element */}
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-neon-green/5 rounded-full blur-3xl pointer-events-none"></div>
    </footer>
  );
};
